<script setup lang="ts">
import {
    CircleHelp,
    CircleUserRound,
    LogOutIcon,
    Milestone,
    Moon,
    Settings,
    Sun
} from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import SettingsDialog from '@/components/settingsMenu/SettingsDialog.vue';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import Separator from '@/components/ui/separator/Separator.vue';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger } from '@/components/ui/tooltip';
import Version from '@/components/Version.vue';
import { isFeedbackEnabled, openFeedbackForm } from '@/plugins/sentry';
import { routes } from '@/router';
import { canAccessRoute } from '@/routerAccess';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useThemeStore } from '@/stores/theme';
import { useUserStore } from '@/stores/user';

import TooltipContent from '../ui/tooltip/TooltipContent.vue';

const tenantStore = useTenantStore();
const user = useUserStore();
const auth = useAuthStore();
const accessContextStore = useAccessContextStore();
const router = useRouter();
const theme = useThemeStore();

const props = defineProps<{
    isSidebarOpen?: boolean;
}>();

const isUserMenuOpen = ref<boolean>(false);
const isSettingsDialogOpen = ref<boolean>(false);

const fullName = computed(() => {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
});

const userDisplayName = computed(() => {
    const name = (user.name ?? '').trim();
    if (name) return name;

    if (fullName.value) return fullName.value;

    const email = (user.email ?? '').trim();
    if (email) return email;

    return 'User';
});

const userMenuRoutes = computed(() => {
    return routes.filter((route) => {
        return (
            route.meta?.showInUserMenu &&
            canAccessRoute(route, {
                isAuthenticated: auth.isAuthenticated,
                tenantPermissions: {
                    tenantAdmin: tenantStore.tenantAdmin,
                    tenantElqano: tenantStore.tenantElqano
                },
                accessEntitlements: {
                    loaded: accessContextStore.loaded,
                    hasActiveSubscription:
                        accessContextStore.hasActiveSubscription,
                    hasCompletedOnboarding:
                        accessContextStore.hasCompletedOnboarding
                }
            })
        );
    });
});

const routing = (routePath: string) => {
    isUserMenuOpen.value = false;
    router.push(routePath);
};

const handleLogout = async () => {
    const redirectPath = await auth.handleLogout();
    if (redirectPath) {
        await router.replace(redirectPath);
    }
};

const openSettingsDialog = () => {
    isUserMenuOpen.value = false;
    isSettingsDialogOpen.value = true;
};

// Stripe plans only, and the Teams embed sets no Sentry user to attach.
const feedbackEnabled = computed(
    () =>
        isFeedbackEnabled() &&
        accessContextStore.billingProvider === 'stripe' &&
        !auth.isInsideTeams
);

const openFeedback = () => {
    isUserMenuOpen.value = false;
    void openFeedbackForm();
};
</script>

<template>
    <Popover v-model:open="isUserMenuOpen">
        <PopoverTrigger as-child>
            <div class="w-full">
                <Tooltip :disabled="props.isSidebarOpen || isUserMenuOpen">
                    <TooltipTrigger as-child>
                        <Button
                            variant="link"
                            class="user_menu w-full flex gap-2 justify-start items-center transition-all h-fit text-foreground px-0"
                            :class="{
                                'px-3 bg-primary10 hover:bg-primary20':
                                    props.isSidebarOpen
                            }"
                        >
                            <CircleUserRound class="h-6 w-6 shrink-0" />
                            <p
                                class="truncate whitespace-nowrap transition-opacity duration-300"
                                :class="
                                    props.isSidebarOpen
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                "
                            >
                                {{ userDisplayName }}
                            </p>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                        <p>{{ userDisplayName }}</p>
                    </TooltipContent>
                </Tooltip>
            </div>
        </PopoverTrigger>
        <PopoverContent class="flex flex-col gap-3 items-start px-2 py-3">
            <div
                class="user_menu_entries w-full flex flex-col gap-1 items-start"
            >
                <Button
                    variant="ghost"
                    class="w-full flex gap-2 hover:bg-primary20 justify-start transition-all text-muted-foreground"
                    @click="openSettingsDialog"
                >
                    <Settings class="h-5 w-5" />
                    Settings
                </Button>
                <template v-if="feedbackEnabled">
                    <Separator />
                    <Button
                        variant="ghost"
                        class="w-full flex gap-2 hover:bg-primary20 justify-start transition-all text-muted-foreground"
                        @click="openFeedback"
                    >
                        <CircleHelp class="h-5 w-5" />
                        Need help?
                    </Button>
                </template>
                <Separator v-if="userMenuRoutes.length > 0" />
                <Button
                    v-for="route in userMenuRoutes"
                    :key="route.name"
                    variant="ghost"
                    class="w-full flex gap-2 hover:bg-primary20 justify-start transition-all text-muted-foreground"
                    @click="routing(route.path)"
                >
                    <component :is="route.meta?.icon" class="h-5 w-5" />{{
                        route.name
                    }}
                </Button>
                <Separator />
                <div
                    class="flex gap-2 justify-between w-full items-center transition-all text-muted-foreground h-fit px-4 py-2"
                >
                    <div class="flex gap-2 items-center">
                        <Sun v-if="!theme.isDark" class="h-5 w-5" />
                        <Moon v-else class="h-5 w-5" />
                        <p class="text-muted-foreground text-sm font-medium">
                            {{ theme.isDark ? 'Dark mode' : 'Light mode' }}
                        </p>
                    </div>
                    <Switch v-model="theme.isDark" size="xs" />
                </div>
                <Separator />
                <Button
                    v-if="!auth.isInsideTeams"
                    variant="ghost"
                    class="flex hover:bg-primary20 justify-start w-full items-center gap-2 transition-all text-muted-foreground h-fit"
                    @click="handleLogout()"
                >
                    <LogOutIcon class="h-4 w-4" />
                    Logout
                </Button>
                <Separator v-if="tenantStore.tenantElqano" />
            </div>
            <Button
                v-if="tenantStore.tenantElqano"
                variant="link"
                class="flex items-center gap-2 transition-all text-placeholder text-xs h-fit px-3"
            >
                <Milestone class="h-4 w-4" />
                <Version />
            </Button>
        </PopoverContent>
    </Popover>
    <SettingsDialog v-model:open="isSettingsDialogOpen" />
</template>
